import React, { useState } from "react";

import { Button, InfoMessage } from "@amphore-dev/amphore-lib";
import { useFormikContext } from "formik";
import { t } from "i18next";
import { toast } from "react-toastify";

import { useMutation } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";
import InvoicesService from "@/services/InvoicesService";

import { usePlanGate } from "@/hooks/usePlanGate";

import { PlanGate } from "@/components/organisms/PlanGate/PlanGate";

import { PLAN_NAMES } from "@/constants/CPlans";

import { TFinalizeInvoiceRequest, TInvoiceFormValues } from "@/types";

import { promiseToast } from "@/modules/index";
import {
	computeInvoiceTotals,
	downloadBlob,
	linesToClipboardText,
} from "@/utils";

import { InvoiceFinalizeModal } from "./InvoiceFinalizeModal";
import { useInvoiceForm } from "./InvoiceFormContext";
import { InvoicePreviewModal } from "./InvoicePreviewModal";

interface IInvoiceActionsBarProps {
	onSaveDraft: () => Promise<unknown>;
	onFinalize: (values: TFinalizeInvoiceRequest) => Promise<unknown>;
	onSetPaid: (paid: boolean) => Promise<unknown>;
	isSaving: boolean;
	isFinalizing: boolean;
}

/**
 * What can be done with the invoice at its current stage. A draft: copy
 * the lines, preview, save, finalize. Issued: copy, download, mark paid.
 */
export const InvoiceActionsBar: React.FC<IInvoiceActionsBarProps> = ({
	onSaveDraft,
	onFinalize,
	onSetPaid,
	isSaving,
	isFinalizing,
}) => {
	const { values, validateForm, setTouched } =
		useFormikContext<TInvoiceFormValues>();
	const { existing, isLocked, billing, isBusy } = useInvoiceForm();
	// Off-plan the draft stays editable; preview, finalize and download
	// lock and open the upgrade modal instead.
	const invoicing = usePlanGate("invoices");
	const [isFinalizeOpen, setIsFinalizeOpen] = useState(false);
	const [previewFile, setPreviewFile] = useState<File | null>(null);

	// The template wants the full parties and the issuer's billing
	// settings; the form only holds the cards. Nothing is saved.
	const previewMutation = useMutation({
		mutationFn: async () => {
			if (!values.company)
				throw new Error(t("invoices.form.companyRequired"));
			const [company, client, billingSettings] = await Promise.all([
				CompaniesService.fetchCompany(values.company.id),
				values.client
					? CompaniesService.fetchCompany(values.client.id)
					: Promise.resolve(null),
				CompaniesService.fetchBilling(values.company.id),
			]);
			const totals = computeInvoiceTotals(values.lines);
			const response = await InvoicesService.preview({
				number: existing?.number ?? null,
				isDraft: true,
				issuedAt: values.issuedAt || null,
				dueAt: values.dueAt || null,
				currency: values.currency,
				company,
				client,
				billing: billingSettings,
				lines: totals.lines,
				totalHt: totals.totalHt,
				totalVat: totals.totalVat,
				totalTtc: totals.totalTtc,
				vatBreakdown: totals.vatBreakdown,
				notes: values.notes || null,
				filesToFetch: {
					companyLogo: company.logo ?? null,
					clientLogo: client?.logo ?? null,
				},
			});
			return new File([response.data], "invoice.pdf", {
				type: "application/pdf",
			});
		},
		onSuccess: setPreviewFile,
	});

	const downloadMutation = useMutation({
		mutationFn: () => InvoicesService.generateById(existing!.id),
		onSuccess: (response) => {
			const disposition = response.headers["content-disposition"];
			const match = disposition?.match(/filename="(.+)"/);
			downloadBlob(response.data, match?.[1] ?? "invoice.pdf");
		},
	});

	const copyLines = async () => {
		const text = linesToClipboardText(values.lines, [
			t("invoices.lines.label"),
			t("invoices.lines.quantity"),
			t("invoices.lines.unit"),
			t("invoices.lines.unitPrice"),
			t("invoices.lines.vat"),
			t("invoices.lines.amount"),
		]);
		await navigator.clipboard.writeText(text);
		toast.success(t("invoices.actions.copied"));
	};

	// The modal opens on a form that would save: the same checks as a
	// draft save, surfaced on the fields.
	const openFinalize = async () => {
		const errors = await validateForm();
		if (Object.keys(errors).length > 0) {
			setTouched({ company: true, lines: true } as never, false);
			return;
		}
		setIsFinalizeOpen(true);
	};

	const busy =
		isBusy || previewMutation.isPending || downloadMutation.isPending;

	return (
		<>
			{isLocked && (
				<InfoMessage color="info" picto="lock" className="mb-4">
					{t("invoices.finalized")}
				</InfoMessage>
			)}
			{!isLocked && invoicing.locked && (
				<InfoMessage color="info" picto="lock" className="mb-4">
					{t("invoices.offPlan", {
						plan: invoicing.requiredPlan
							? PLAN_NAMES[invoicing.requiredPlan]
							: "",
					})}
				</InfoMessage>
			)}
			<div className="flex flex-wrap items-center justify-end gap-2">
				<Button
					variant="outline"
					picto="copy"
					onClick={() => copyLines()}
					disabled={values.lines.length === 0}
				>
					{t("invoices.actions.copyLines")}
				</Button>

				{isLocked ? (
					<>
						<PlanGate on="invoices">
							<Button
								variant="outline"
								picto="download"
								isLoading={downloadMutation.isPending}
								disabled={busy}
								onClick={() =>
									promiseToast(downloadMutation.mutateAsync())
								}
							>
								{t("invoices.actions.download")}
							</Button>
						</PlanGate>
						<Button
							color={
								existing?.status === "paid"
									? "neutral"
									: "success"
							}
							picto={
								existing?.status === "paid"
									? "refresh"
									: "checkCircle"
							}
							disabled={busy}
							onClick={() =>
								promiseToast(
									onSetPaid(existing?.status !== "paid")
								)
							}
						>
							{existing?.status === "paid"
								? t("invoices.actions.markUnpaid")
								: t("invoices.actions.markPaid")}
						</Button>
					</>
				) : (
					<>
						<PlanGate on="invoices">
							<Button
								variant="outline"
								picto="eye"
								isLoading={previewMutation.isPending}
								disabled={busy || !values.company}
								onClick={() =>
									promiseToast(previewMutation.mutateAsync())
								}
							>
								{t("invoices.actions.preview")}
							</Button>
						</PlanGate>
						<Button
							variant="outline"
							picto="save"
							isLoading={isSaving}
							disabled={busy}
							onClick={() => onSaveDraft()}
						>
							{t("invoices.actions.saveDraft")}
						</Button>
						<PlanGate on="invoices">
							<Button
								picto="lock"
								disabled={busy}
								onClick={() => openFinalize()}
							>
								{t("invoices.actions.finalize")}
							</Button>
						</PlanGate>
					</>
				)}
			</div>

			<InvoicePreviewModal
				file={previewFile}
				onClose={() => setPreviewFile(null)}
				footer={
					<>
						<Button
							color="white"
							onClick={() => setPreviewFile(null)}
						>
							{t("actions.cancel")}
						</Button>
						<Button
							picto="lock"
							onClick={() => {
								setPreviewFile(null);
								openFinalize();
							}}
						>
							{t("invoices.actions.finalize")}
						</Button>
					</>
				}
			/>

			<InvoiceFinalizeModal
				isOpen={isFinalizeOpen}
				onClose={() => setIsFinalizeOpen(false)}
				companyId={values.company?.id}
				issuedAt={values.issuedAt}
				dueAt={values.dueAt}
				paymentTermsDays={billing?.paymentTermsDays ?? 30}
				isLoading={isFinalizing}
				onConfirm={(finalizeValues) =>
					promiseToast(
						onFinalize(finalizeValues).then(() =>
							setIsFinalizeOpen(false)
						)
					)
				}
			/>
		</>
	);
};
